import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { EIntakeType } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";

/**
 * The language this candidate reads, as one of the three the office writes in.
 *
 * Contacts do not all carry a tidy `en_UK`. `en_US` is stored too, which is why
 * the broker's notification preferences are read under that key as well, and a
 * bare `en` is no surprise either. Matching on the language rather than on the
 * whole tag keeps those together: otherwise an `en_US` candidate gets a box of
 * their own, labelled with the generic fallback because no English label
 * matches, sitting beside the `en_UK` box saying the same thing.
 */
const LOCALE_BY_LANGUAGE: { [language: string]: string } = {
  nl: ELocale.NL,
  fr: ELocale.FR,
  en: ELocale.EN,
};

export const getApplicationLocale = (application: IApplication): string => {
  const stored = `${application?.contacts?.[0]?.language || ""}`;
  const language = stored.toLowerCase().split(/[-_]/)[0];

  // Both fallbacks mirror the API, so the subject it translates and the body
  // sent from here always land in the same language: nothing stored means
  // Dutch, and something stored that is not one of the three means English.
  // Diverging here would reintroduce the exact split this ticket is about, on
  // the rarest candidates and therefore the ones nobody would notice.
  if (!stored) {
    return ELocale.NL;
  }

  return LOCALE_BY_LANGUAGE[language] || ELocale.EN;
};

/**
 * The note the broker wrote about this candidate.
 *
 * Two places hold one: the application's own internal note, and a note on
 * the main contact from before that field existed. The candidate row has
 * always preferred the first and fallen back to the second, and the
 * send-to-owner window prefills from the same helper so the broker is not
 * shown one note on the row and a different one in the window (DEV-3326).
 */
export const getInternalNote = (application: IApplication): string =>
  `${
    application?.otherApplicationsMeta?.internalNote ||
    application?.contacts?.[0]?.note ||
    ""
  }`;

export const getStepsForIntakeType = (
  intakeType: EIntakeType,
): EIntakeStep[][] => {
  switch (intakeType) {
    case EIntakeType.TwoPhases:
      return [
        [
          EIntakeStep.candidateVisitor,
          EIntakeStep.waitingList,
          EIntakeStep.invited,
          EIntakeStep.visitor,
        ],
        [EIntakeStep.candidateTenant, EIntakeStep.tenant],
        [EIntakeStep.notEligible],
      ];
    case EIntakeType.TwoPhasesWithVisit:
      return [
        [
          EIntakeStep.candidateVisitor,
          EIntakeStep.waitingList,
          EIntakeStep.planAsVisitor,
          EIntakeStep.invited,
          EIntakeStep.visitor,
        ],
        [EIntakeStep.candidateTenant, EIntakeStep.tenant],
        [EIntakeStep.notEligible],
      ];
    default:
      return [
        [EIntakeStep.candidateTenant],
        [EIntakeStep.tenant],
        [EIntakeStep.notEligible],
      ];
  }
};

export const getPhaseForStep = (intakeType: EIntakeType, step: EIntakeStep) => {
  const steps = getStepsForIntakeType(intakeType);
  return steps.findIndex(steps => steps.includes(step));
};

export interface IFlowItem {
  id: EIntakeStep;
  allowedSteps: EIntakeStep[];
}

export const getFlows = (
  intakeType: EIntakeType,
  hasCalendarSync?: boolean,
  hasRentMatch?: boolean,
): {
  visitorSteps?: IFlowItem[];
  candidateSteps: IFlowItem[];
  notEligibleSteps: IFlowItem[];
} => {
  const flows = {
    [EIntakeType.OnePhase]: {
      candidateSteps: [
        {
          id: EIntakeStep.candidateTenant,
          allowedSteps: [EIntakeStep.tenant, EIntakeStep.notEligible],
        },
        { id: EIntakeStep.tenant, allowedSteps: [EIntakeStep.notEligible] },
      ],
      notEligibleSteps: [
        {
          id: EIntakeStep.notEligible,
          allowedSteps: [
            EIntakeStep.candidateTenant,
            EIntakeStep.candidateVisitor,
            EIntakeStep.tenant,
          ],
        },
      ],
    },
    [EIntakeType.TwoPhases]: {
      visitorSteps: [
        {
          id: EIntakeStep.candidateVisitor,
          allowedSteps: [
            EIntakeStep.visitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.waitingList,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.visitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.invited,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.visitor,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.visitor,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
      ],
      candidateSteps: [
        {
          id: EIntakeStep.candidateTenant,
          allowedSteps: [EIntakeStep.tenant, EIntakeStep.notEligible],
        },
        { id: EIntakeStep.tenant, allowedSteps: [EIntakeStep.notEligible] },
      ],
      notEligibleSteps: [
        {
          id: EIntakeStep.notEligible,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.tenant,
          ],
        },
      ],
    },
    [EIntakeType.TwoPhasesWithVisit]: {
      visitorSteps: [
        {
          id: EIntakeStep.candidateVisitor,
          allowedSteps: [
            EIntakeStep.visitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.waitingList,
          allowedSteps: [
            EIntakeStep.visitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.planAsVisitor,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.invited,
            EIntakeStep.visitor,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.invited,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.visitor,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
        {
          id: EIntakeStep.visitor,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.planAsVisitor,
            EIntakeStep.invited,
            EIntakeStep.candidateTenant,
            EIntakeStep.notEligible,
          ],
        },
      ],
      candidateSteps: [
        {
          id: EIntakeStep.candidateTenant,
          allowedSteps: [EIntakeStep.tenant, EIntakeStep.notEligible],
        },
        { id: EIntakeStep.tenant, allowedSteps: [EIntakeStep.notEligible] },
      ],
      notEligibleSteps: [
        {
          id: EIntakeStep.notEligible,
          allowedSteps: [
            EIntakeStep.candidateVisitor,
            EIntakeStep.waitingList,
            EIntakeStep.tenant,
          ],
        },
      ],
    },
  };

  const selectedFlow = flows[intakeType];

  if (hasCalendarSync === false || hasRentMatch === false) {
    const filterStep = (step: EIntakeStep) => {
      let include = true;
      if (hasCalendarSync === false) {
        include = include && step !== EIntakeStep.invited;
      }
      if (hasRentMatch === false) {
        include = include && step !== EIntakeStep.invited;
      }
      return include;
    };

    const filterSteps = (stepsArray?: IFlowItem[]) => {
      if (!stepsArray) return undefined;
      return stepsArray
        .filter(item => filterStep(item.id))
        .map(item => ({
          ...item,
          allowedSteps: item.allowedSteps.filter(filterStep),
        }));
    };

    return {
      visitorSteps: filterSteps((selectedFlow as any).visitorSteps),
      candidateSteps: filterSteps(selectedFlow.candidateSteps) || [],
      notEligibleSteps: filterSteps(selectedFlow.notEligibleSteps) || [],
    };
  }

  return selectedFlow;
};

// Nothing here decides whether profile data may be shown any more. The sender
// filters at submission and the row states what it was asked for, so a screen
// that also decided was a second opinion that could disagree with the first,
// and did: the two candidate lists gated on different phases and contradicted
// each other and their own detail panels (DEV-3135).
//
// What remains is the reading of what arrived, and the handling of absence.

/**
 * What a row says about a set of documents. Null and zero both render nothing,
 * but they are not the same fact and the code should not collapse them: null is
 * a row submitted before the counts existed, zero is a submission that asked
 * for no documents at all.
 */
export enum EDocumentRequirement {
  Unknown = "unknown",
  NoneRequired = "noneRequired",
  Required = "required",
}

export const getDocumentRequirement = (
  required?: number | null,
): EDocumentRequirement => {
  if (typeof required !== "number") {
    return EDocumentRequirement.Unknown;
  }

  return required > 0
    ? EDocumentRequirement.Required
    : EDocumentRequirement.NoneRequired;
};

/**
 * A fraction is rendered only when documents were actually asked for. This one
 * rule replaces every per-phase gate the dashboard used to carry: a visit
 * request asks for none and so shows none, on every tab including the archive.
 */
export const shouldShowDocumentCount = (
  required?: number | null,
): required is number =>
  getDocumentRequirement(required) === EDocumentRequirement.Required;

/**
 * The legacy list shows one combined fraction where the Rentmatch list shows
 * two, so it adds the counts together. Null on both sides stays null, since a
 * row from before the contract knows nothing about either half; a null beside a
 * number is treated as the zero it renders as.
 */
export const sumDocumentRequirements = (
  requiredId?: number | null,
  requiredIncome?: number | null,
) => {
  if (typeof requiredId !== "number" && typeof requiredIncome !== "number") {
    return null;
  }

  return (requiredId ?? 0) + (requiredIncome ?? 0);
};

/**
 * Red on the delivered number as soon as one document is short (DEV-3075).
 */
export const isDocumentCountIncomplete = (
  delivered: number,
  required?: number | null,
) => shouldShowDocumentCount(required) && delivered < required;

/**
 * Zero adults means the residents question was never put, not that nobody
 * lives there: the input has a minimum of one adult, so a submitted household
 * is always at least one person. The one flow that skips the question entirely
 * is a Brussels visit request.
 *
 * Children are not part of this test. Zero children is a real answer, and it
 * never appears on its own because it sits inside the block this guards.
 */
export const shouldShowHousehold = (adults: number) => adults > 0;

/**
 * Same reasoning for income: a required income needs at least one source worth
 * at least one, and the primary contact always counts as requiring one, so a
 * total of zero proves the question was never put. That happens in the visitor
 * phase at a broker with optional income. Hiding it also hides the red marking,
 * which would otherwise blame a candidate for an answer nobody asked for.
 */
export const shouldShowIncome = (totalIncome: number) => totalIncome > 0;

// The steps a submission counts as a candidacy, mirroring legacy's own rule
// (application-profile.js, `CANDIDACY_STEPS = [2, 3]`). Step 3 is reachable at
// submit time: the tenant phase routes to the resend view, which submits with
// it, so a row submitted that way is a real candidacy that would carry no
// marker if only 2 were accepted. Reusing the backend's pair rather than writing {2} here
// keeps one definition of the word; four of them growing apart is what this
// whole series exists to undo.
const CANDIDACY_SUBMITTED_STEPS: number[] = [
  EIntakeStep.candidateTenant,
  EIntakeStep.tenant,
];

/**
 * Whether the submission that created this row was a candidacy, read from the
 * step it was submitted with. That value does not move when a broker drags the
 * candidate, which is the whole reason it exists.
 */
export const isCandidacySubmission = (submittedStep?: number | null) =>
  typeof submittedStep === "number" &&
  CANDIDACY_SUBMITTED_STEPS.includes(submittedStep);

/**
 * Pets are part of the residents block: one entry per species, identified by
 * "_PETS_" in the id (INTAKE_PETS_DOGS, _CATS, _BIRDS and so on).
 */
export const isPetResident = (residentId: string) =>
  residentId.includes("_PETS_");
